import { AlertTriangle } from 'lucide-react'

import type { PublicSubscription } from '@snootsvet/shared/portal'

import { Badge } from '@/customer/components/ui/badge'

type BadgeSize = 'default' | 'sm' | 'xs'

/**
 * Small pill surfacing a live subscription's Stripe status when it
 * needs the member's attention. Happy-path statuses (`active`,
 * `trialing`) render nothing - the absence of a badge is the signal
 * that everything's fine.
 *
 * Copy + variant are centralised here so any surface that renders a
 * subscription (portal pets index, future billing/invoice pages) sees
 * the same language. `size` mirrors the underlying `Badge` API so
 * callers can shrink the pill for inline usage.
 */
export function SubscriptionStatusBadge({
  subscription,
  size = 'default',
}: {
  subscription: PublicSubscription | null
  size?: BadgeSize
}) {
  if (!subscription) return null
  switch (subscription.status) {
    case 'active':
    case 'trialing':
      return null
    case 'past_due':
    case 'unpaid':
      return (
        <Badge variant="warning" size={size}>
          Overdue
        </Badge>
      )
    case 'canceled':
    case 'incomplete_expired':
      return (
        <Badge variant="neutral" size={size}>
          Cancelled
        </Badge>
      )
    case 'incomplete':
      return (
        <Badge variant="destructive" size={size}>
          <AlertTriangle />
          Payment failed
        </Badge>
      )
    case 'paused':
      return (
        <Badge variant="neutral" size={size}>
          Paused
        </Badge>
      )
    default:
      return null
  }
}
